/**
 * Google Tag Manager Integration
 * Documentation: https://developers.google.com/tag-manager/devguide
 */

// Mesmo formato validado no backend (GoogleTagManagerPixel::CONTAINER_ID_PATTERN).
// O id vai direto na URL de gtm.js, então nada fora do padrão oficial é carregado.
const CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]{4,12}$/i;

/** Marca página ou bloco que coleta PAN/CVV em input nosso. */
export const CHECKOUT_CARD_DATA_SELECTOR = '[data-checkout-card-data]';

const STORED_CARD_KEY = 'credit_card';
const CARD_SECRET_FIELDS = ['number', 'cvv', 'expiration'];

/**
 * Checkout que oferece cartão coleta PAN na própria página.
 * Só libera GTM quando o cartão está explicitamente desligado.
 */
export function checkoutCollectsCardData(checkout) {
    if (!checkout || typeof checkout !== 'object') {
        return true;
    }

    const flag = checkout.credit_card;

    return flag !== false && flag !== 0 && flag !== '0';
}

/** GTM do lojista só entra em checkout que não coleta dado de cartão. */
export function canLoadMerchantGtm(checkout) {
    return !checkoutCollectsCardData(checkout);
}

export function documentHasCardData(doc = typeof document !== 'undefined' ? document : null) {
    if (!doc || typeof doc.querySelector !== 'function') {
        return false;
    }

    return Boolean(doc.querySelector(CHECKOUT_CARD_DATA_SELECTOR));
}

function readStoredCard() {
    if (typeof localStorage === 'undefined') {
        return null;
    }

    const raw = localStorage.getItem(STORED_CARD_KEY);

    if (!raw) {
        return null;
    }

    try {
        const data = JSON.parse(raw);

        return data && typeof data === 'object' ? data : undefined;
    } catch {
        return undefined;
    }
}

export function storedCardHasSecret() {
    const data = readStoredCard();

    if (data === undefined) {
        return true;
    }

    if (!data) {
        return false;
    }

    return CARD_SECRET_FIELDS.some((field) => String(data[field] ?? '').replace(/\s/g, '') !== '');
}

export function cardWithoutSecrets(card) {
    return {
        ...(card && typeof card === 'object' ? card : {}),
        number: '',
        cvv: '',
        expiration: '',
    };
}

/** Tira PAN/CVV/validade do localStorage. Não carrega script nenhum. */
export function purgeStoredCardSecrets() {
    if (typeof localStorage === 'undefined') {
        return;
    }

    const data = readStoredCard();

    if (data === undefined) {
        localStorage.removeItem(STORED_CARD_KEY);
        return;
    }

    if (!data || !storedCardHasSecret()) {
        return;
    }

    localStorage.setItem(STORED_CARD_KEY, JSON.stringify(cardWithoutSecrets(data)));
}

export function initGTM(containerId) {
    if (typeof window === 'undefined' || !containerId) {
        return;
    }

    if (!CONTAINER_ID_PATTERN.test(String(containerId))) {
        console.error('GTM container id inválido, script não carregado');
        return;
    }

    if (documentHasCardData() || storedCardHasSecret()) {
        console.error('GTM do lojista não carregado: página com dado de cartão');
        return;
    }

    window.dataLayer = window.dataLayer || [];

    if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${containerId}"]`)) {
        return;
    }

    // GTM Container Code
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer',containerId);
}

export function pushToDataLayer(event) {
    if (typeof window === 'undefined' || !window.dataLayer) {
        console.error('GTM dataLayer not initialized');
        return;
    }
    
    window.dataLayer.push(event);
}

export function dispatchGTMPageView({ pagePath = null, pageTitle = null } = {}) {
    pushToDataLayer({
        event: 'page_view',
        pagePath: pagePath || window.location.pathname,
        pageTitle: pageTitle || document.title,
    });
}

export function dispatchGTMViewItem(ecommerceData = {}) {
    const ecommerce = {
        currency: 'BRL',
        ...ecommerceData,
    };

    pushToDataLayer({
        event: 'view_item',
        ecommerce: ecommerce,
    });
}

export function dispatchGTMAddToCart(ecommerceData = {}) {
    const ecommerce = {
        currency: 'BRL',
        ...ecommerceData,
    };

    pushToDataLayer({
        event: 'add_to_cart',
        ecommerce: ecommerce,
    });
}

export function dispatchGTMBeginCheckout(ecommerceData = {}) {
    const ecommerce = {
        currency: 'BRL',
        ...ecommerceData,
    };

    pushToDataLayer({
        event: 'begin_checkout',
        ecommerce: ecommerce,
    });
}

export function dispatchGTMAddPaymentInfo(ecommerceData = {}) {
    const ecommerce = {
        currency: 'BRL',
        ...ecommerceData,
    };

    pushToDataLayer({
        event: 'add_payment_info',
        ecommerce: ecommerce,
    });
}

export function dispatchGTMPurchase(ecommerceData = {}) {
    const ecommerce = {
        currency: 'BRL',
        ...ecommerceData,
    };

    pushToDataLayer({
        event: 'purchase',
        ecommerce: ecommerce,
    });
}
